import { DATA } from "@/common/data";
import { serviceMap } from "@/common/constants";
import BorderedIcon, { iconSize } from "@/components/BorderedIcon";
import { loopValue } from "@/mono/lib/common";
import { useTicker } from "@/mono/lib/hooks/useRaf";
import useResize from "@/mono/lib/hooks/useResize";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import useSubscribe from "@/mono/lib/hooks/useSubscribe";
import { lerp } from "@/mono/lib/math";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import {
  ScrollScene,
  UseCanvas,
} from "@/mono/third-party/r3f-scroll-rig-custom";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import styled from "styled-components";
import { ServicesGLImage, serviceStore } from "./HomeWhatWeDo";
import useIsomorphicLayoutEffect from "@/mono/lib/hooks/useIsomorphicLayoutEffect";
import { TransitionLink } from "mono/components/TransitionLink";
import { mediaTabletOnly } from "mono/lib/responsive";

const SERVICE_IMAGE_URLS = {
  campaign: "/images/services/campaign.webp",
  identity: "/images/services/identity.webp",
  webdesign: "/images/services/web-design.webp",
  "photo-video": "/images/services/photo-video.webp",
  strategy: "/images/services/strategy.webp",
};

const Navigation = forwardRef((props, ref) => {
  const x = useRef(0);
  const _x = useRef(0);

  const inner = useRef();
  const texts = useRef([]);
  const textWidths = useRef([]);
  const gap = useRef(0);
  const wrapper = useRef();

  const currIndex = serviceStore((s) => s.currIndex);

  // TODO: Maybe create a hook or util function that handles lerps with easings?

  function moveService(direction) {
    const currIndex = serviceStore.getState().currIndex + direction;
    const nextIndex = loopValue(
      currIndex,
      DATA.pages.home.whatWeDo.services.length
    );

    serviceStore.setState({
      currIndex: nextIndex,
      prevIndex: serviceStore.getState().currIndex,
    });
  }

  useSubscribe(serviceStore, (state) => {
    if (state.prevIndex === state.currIndex) return;

    const wrapperWidth = wrapper.current?.offsetWidth || 0;
    const textWidth = textWidths.current[state.currIndex];

    // Calculate the starting position (half of the wrapper width)
    const startPosition = wrapperWidth / 2;

    // Calculate the total width up to the current text including gaps
    const totalWidth = textWidths.current
      .slice(0, state.currIndex)
      .reduce((sum, width) => sum + width + gap.current, 0);

    // Add half of the current text width to center it
    x.current = totalWidth + textWidth / 2 - startPosition;

    texts.current[state.prevIndex]?.classList.remove("active");
    texts.current[state.currIndex]?.classList.add("active");
  });

  const { start, stop } = useTicker(({ delta }) => {
    if (!inner.current) return;

    _x.current = lerp(_x.current, x.current, 0.05, delta);

    inner.current.style.transform = `translateX(${-_x.current}px)`;
  });

  useImperativeHandle(ref, () => ({
    start,
    stop,
  }));

  useResize(
    () => {
      texts.current.forEach((text, index) => {
        if (!text) return;
        const { width } = text.getBoundingClientRect();
        textWidths.current[index] = width;
      });

      const secondTextLeft = texts.current[1]?.getBoundingClientRect()?.left;
      const firstTextRight = texts.current[0]?.getBoundingClientRect()?.right;

      gap.current = secondTextLeft - firstTextRight;
    },
    {
      immediate: true,
      debounce: 10,
    },
    [texts.current, wrapper.current, inner.current, texts.current.length]
  );

  const handleClickLeft = useCallback(() => {
    moveService(-1);
  }, [currIndex]);

  const handleClickRight = useCallback(() => {
    moveService(1);
  }, [currIndex]);

  useEffect(() => {
    if (!wrapper.current || !texts.current.length) return;

    texts.current.forEach((text) => {
      text.classList.remove("active");
    });

    texts.current[0].classList.add("active");

    const wrapperWidth = wrapper.current.offsetWidth;
    const textWidth = textWidths.current[0]; // Get first text width
    const startPosition = wrapperWidth / 2;

    x.current = textWidth / 2 - startPosition;
    _x.current = x.current; // Set the lerped position as well to avoid animation

    if (inner.current) {
      inner.current.style.transform = `translateX(${-x.current}px)`;
    }

    return () => {
      _x.current = 0;
      x.current = 0;
    };
  }, []);

  return (
    <NavigationWrapper>
      <BorderedIcon
        className="what-we-do-mobile__navigation__icon icon-left"
        icon="chevronLeft"
        onClick={handleClickLeft}
      />
      <div
        className="what-we-do-mobile__navigation__inner-wrapper"
        ref={wrapper}
      >
        <div className="fade-overlay"></div>
        <div className="what-we-do-mobile__navigation__inner" ref={inner}>
          {DATA.pages.home.whatWeDo.services.map((service, index) => (
            <div key={index} className="what-we-do-mobile__navigation__item">
              <TransitionLink href={`/service/${service.slug}`}>
                <span
                  ref={(ref) => (texts.current[index] = ref)}
                  key={index}
                  className={index === 0 ? "active" : ""}
                >
                  {service.title}
                </span>
              </TransitionLink>
            </div>
          ))}
        </div>
      </div>
      <BorderedIcon
        className="what-we-do-mobile__navigation__icon icon-right"
        icon="chevronRight"
        onClick={handleClickRight}
      />
    </NavigationWrapper>
  );
});

function ServicesDOMImageMobile() {
  const imgRef = useRef();
  const serviceKeys = Object.keys(serviceMap);

  useSubscribe(serviceStore, ({ currIndex }) => {
    if (!imgRef.current) return;
    const key = serviceKeys[currIndex]?.toLowerCase();
    const url = SERVICE_IMAGE_URLS[key];
    if (url) imgRef.current.src = url;
  });

  const initialKey = serviceKeys[0]?.toLowerCase();

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden", borderRadius: "10px" }}>
      <img
        ref={imgRef}
        src={SERVICE_IMAGE_URLS[initialKey]}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  );
}

export default function WhatWeDoServicesMobile({ serviceImages }) {
  const { services } = DATA.pages.home.whatWeDo;
  const noWebGLImages = useGlobalStore((s) => s.noWebGLImages);

  const track = useRef();
  const glImage = useRef();
  const navigation = useRef();

  useScrollRigScrollTrigger({
    el: track,
    handleIn: () => {
      navigation.current?.start();
      if (!noWebGLImages) glImage.current?.animateIn();
    },

    handleOut: () => {
      navigation.current.stop();
    },
  });

  const currentService = serviceStore((s) => s.currIndex);

  return (
    <>
      <Wrapper ref={track}>
        <TransitionLink href={`/service/${services[currentService].slug}`}>
          {noWebGLImages ? (
            <ServicesDOMImageMobile />
          ) : (
            <UseCanvas>
              <ScrollScene track={track}>
                {(props) => (
                  <ServicesGLImage
                    serviceImages={serviceImages}
                    track={track}
                    ref={glImage}
                    useHover={false}
                    {...props}
                  />
                )}
              </ScrollScene>
            </UseCanvas>
          )}
        </TransitionLink>
      </Wrapper>

      <Navigation ref={navigation} />
    </>
  );
}

const Wrapper = styled.div`
  margin-top: 140rem;
  width: 100%;
  aspect-ratio: 1/1;

  ${mediaTabletOnly(`
    aspect-ratio: 16/9;
  `)}

  a {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const NavigationWrapper = styled.div`
  margin: 100rem auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${iconSize};

  .what-we-do-mobile__navigation__icon {
    position: absolute;
    top: 0;
    z-index: 2;

    &.icon-left {
      left: 0;
    }

    &.icon-right {
      right: 0;
    }
  }

  --innerMargin: 50rem;
  .what-we-do-mobile__navigation__inner-wrapper {
    overflow: hidden;
    width: calc(100% - ${iconSize} * 2 - var(--innerMargin) * 2);
    mask-image: linear-gradient(
      to right,
      transparent,
      black 20%,
      black 80%,
      transparent
    );
    position: relative;
  }

  .fade-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
  }

  .what-we-do-mobile__navigation__item {
    span {
      white-space: nowrap;
    }
  }

  .what-we-do-mobile__navigation__inner {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-flow: row nowrap;
    --gap: 50rem;
    gap: var(--gap);
    margin-left: 0;

    span {
      text-align: center;
      ${setTypography("whatWeDoMobileSlider")}
      color: ${({ theme }) => theme.theme.inactiveTextColor};
      font-weight: 400;
      text-transform: none;

      transition: color 0.4s cubic-bezier(0.77, 0, 0.175, 1),
        opacity 0.4s cubic-bezier(0.77, 0, 0.175, 1);
      opacity: 0.2;

      &.active {
        opacity: 1;
        color: ${({ theme }) => theme.theme.textColor};
      }
    }
  }
`;
